import React, { useEffect, useState } from "react";
import { apiService } from "@services/ApiService";
import { useNavigate } from "react-router-dom";
import { useUser } from "@services/UserContext";
import ConfirmDeleteModal from "@shared/components/ConfirmationModal";
import { SimplifiedProject } from "@shared/interfaces/ProjectsFiles";
import SubTitleHeader from "@shared/components/SubTitleHeader";
import ProjectsTabs from "@pages/Project/components/Tabs/ProjectsTabs";
import AvatarList from "@pages/Home/components/AvatarList";
import DashboardSkeleton from "@pages/Project/components/Skeletons/dashboardskeleton";
import ProjectCardSkeleton from "@pages/Project/components/Skeletons/projectcardskeleton";
import { useProject } from "@utils/useProject";
import Pagination from "@shared/components/Pagination";
import { Tooltip } from "flowbite-react";
import NewItemButton from "@shared/components/NewItemButton";
import PageContentFilterDropdown from "@shared/components/PageContentFilterDropdown";

const Dashboard: React.FC = () => {
  const [projects, setProjects] = useState<SimplifiedProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [firstLoad, setFirstLoad] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(
    null
  );
  const navigate = useNavigate();
  const { user } = useUser();
  const [activeTab, setActiveTab] = useState<"private" | "All" | "shared">(
    "shared"
  );
  const [dropdownFilter, setDropdownFilter] = useState<"Oldest" | "Latest">(
    "Latest"
  );

  const { fetchProjects } = useProject();

  const PROJECTS_PER_PAGE = 6;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const totalPages = Math.ceil(totalCount / PROJECTS_PER_PAGE);

  const getProjects = async () => {
    setLoading(true);
    try {
      const offset = (currentPage - 1) * PROJECTS_PER_PAGE;
      const order = dropdownFilter.toLowerCase(); // latest | oldest

      const { projects: fetchedProjects, total_count } = await fetchProjects({
        offset,
        limit: PROJECTS_PER_PAGE,
        order,
        tab: activeTab,
      });

      setProjects(fetchedProjects);
      setTotalCount(total_count);
    } catch (error: any) {
      console.error("Failed to fetch projects:", error);
    } finally {
      setTimeout(() => {
        setFirstLoad(false);
        setLoading(false);
      }, 500);
    }
  };

  const handleCreateProject = () => {
    navigate("/create-project");
  };

  const handleViewClick = (projectId: number) => {
    navigate(`/project/${projectId}`);
  };

  const handleDelete = async () => {
    if (selectedProjectId !== null) {
      try {
        await apiService.delete(`/projects/${selectedProjectId}/`);
        setProjects(
          projects.filter((project) => project.id !== selectedProjectId)
        );
        setIsDeleteModalOpen(false);
      } catch (error: any) {
        console.error("Error deleting project:", error);
      }
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  };

  useEffect(() => {
    getProjects();
  }, [activeTab, dropdownFilter, currentPage]);

  if (loading && firstLoad) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="px-8 pb-3 flex-1 flex flex-col">
      <SubTitleHeader
        title={`Projects`}
        subtitle={`Your project collaboration space`}
        subtitleStyle="text-gray-400 text-sm 2xl:text-base"
        childrenTitle={
          <ProjectsTabs
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            setDropdownFilter={setDropdownFilter as (value: string) => void}
            dropdownFilter={dropdownFilter}
          />
        }
        children={
          <>
            <PageContentFilterDropdown
              dropdownFilter={dropdownFilter}
              setDropdownFilter={setDropdownFilter as (value: string) => void}
            />
            <NewItemButton onClick={handleCreateProject} title="Project" />
          </>
        }
      />

      <div className="flex-1">
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6 ${
            projects.length === 0 ? "min-h-[60vh]" : ""
          }`}
        >
          {loading ? (
            // Show 6 skeletons as placeholders
            Array.from({ length: 6 }).map((_, index) => (
              <ProjectCardSkeleton key={index} />
            ))
          ) : projects.length === 0 ? (
            <div className="col-span-full flex flex-col justify-center my-auto items-center ">
              <img src="/no_files.png" alt="" />
              <h1 className="text-lg font-semibold mb-2 mt-4">
                {activeTab == "shared" ? (
                  <span>No shared projects</span>
                ) : (
                  <span>No projects</span>
                )}
              </h1>
              <p className="text-gray-400">
                {activeTab == "shared" ? (
                  <span>
                    When some shares a project with you, it will appear here.
                  </span>
                ) : (
                  <span>Start by creating a new project.</span>
                )}
              </p>
            </div>
          ) : (
            projects.map((project) => (
              <div
                key={project.id}
                className="p-4  w-100 bg-gray-50 rounded-lg border border-gray-300 hover:shadow hover:bg-gray-100 transition duration-200 ease-in-out cursor-pointer flex flex-col relative"
                onClick={() => handleViewClick(project.id)}
              >
                <div className="flex justify-between items-center h-[32px]">
                  <p className="text-xs text-gray-500">
                    {formatDate(project.created_at)}
                  </p>

                  <AvatarList shared_with_name={project.shared_with} />
                </div>
                <h2 className="font-semibold text-gray-800 text-base 2xl:text-lg my-2 2xl:my-3 flex items-center">
                  <span className="mr-2 text-base 2xl:text-xl">
                    {project.icon.length > 0 ? project.icon : "⭐️"}
                  </span>
                  {project.name}
                </h2>
                <p className="text-[0.78rem] leading-4 2xl:text-sm text-gray-600 line-clamp-4 h-[4rem] 2xl:h-[5rem]">
                  {project.description}
                </p>
                <div className="flex justify-between items-center mt-4 text-xs 2xl:text-sm text-gray-500">
                  <div className="flex items-center space-x-4">
                    <span>
                      Created by{" "}
                      {project.creator.email === user?.email ? (
                        <span>you</span>
                      ) : (
                        <span>{`${project.creator.first_name} ${project.creator.last_name}`}</span>
                      )}
                    </span>
                    {project.is_private !== undefined && (
                      <>
                        {project.is_private ? (
                          <Tooltip
                            content="Private Project"
                            placement="top"
                            style="dark"
                            arrow={false}
                          >
                            <img
                              src="/project/private.png"
                              className="w-5 cursor-pointer"
                              alt="Private Project"
                            />
                          </Tooltip>
                        ) : (
                          <Tooltip
                            content="Public Project"
                            placement="top"
                            style="dark"
                            arrow={false}
                          >
                            <img
                              src="/project/public.png"
                              className="w-5 cursor-pointer"
                              alt="Public Project"
                            />
                          </Tooltip>
                        )}
                      </>
                    )}
                  </div>
                  <span>{project.files_count} files</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}
      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        entityNames={
          projects.find((project) => project.id === selectedProjectId)?.name ||
          ""
        }
      />
    </div>
  );
};

export default Dashboard;
